// ============================================
// HOOK: usePlants
// ============================================
// Archivo: src/hooks/usePlants.js

import { useState, useEffect, useCallback } from 'react';
import { plantService } from '../services/api';
import { mapPlantFromBackend } from '../utils/mappers';
import { toast } from 'react-hot-toast';

console.log('🔧 USEPLANTS VERSION: TODO EN ESPAÑOL');

const validatePlant = (plantData) => {
  const errors = [];

  // Aceptar tanto español (codigo) como inglés (code)
  const code = plantData.code || plantData.codigo;
  console.log('📝 Code extraído:', code);
  if (!code || !code.trim()) {
    errors.push('El código es obligatorio');
  }

  // Aceptar tanto español (nombre) como inglés (name)
  const name = plantData.name || plantData.nombre;
  console.log('📝 Name extraído:', name);
  if (!name || !name.trim()) {
    errors.push('El nombre es obligatorio');
  }
  console.log('❌ Errores:', errors);
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const usePlants = (isAuthenticated) => {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPlants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await plantService.getAll();
      const mapped = data.map(mapPlantFromBackend);
      setPlants(mapped);
    } catch (err) {
      setError(err.message);
      toast.error('Error cargando plantas');
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar plantas al autenticarse
  useEffect(() => {
    if (isAuthenticated) {
      loadPlants();
    }
  }, [isAuthenticated, loadPlants]);

  const createPlant = useCallback(async (plantData) => {
    // Validar
    const validation = validatePlant(plantData);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return { success: false, errors: validation.errors };
    }

    setLoading(true);
    try {
      // PlantData ya viene en español del PlantModal, no mapear
      const created = await plantService.create(plantData);
      const mapped = mapPlantFromBackend(created);
      
      setPlants(prev => [...prev, mapped]);
      toast.success('Planta creada correctamente');
      
      return { success: true, plant: mapped };
    } catch (err) {
      toast.error(err.message || 'Error creando planta');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePlant = useCallback(async (id, plantData) => {
    console.log("updatePlant recibió:", id, plantData);

    const validation = validatePlant(plantData);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return { success: false, errors: validation.errors };
    }

    setLoading(true);
    try {
      // PlantData ya viene en español del PlantModal, no mapear
      const updated = await plantService.update(id, plantData);
      const mapped = mapPlantFromBackend(updated);
      
      setPlants(prev => prev.map(p => p.id === id ? mapped : p));
      toast.success('Planta actualizada correctamente');
      
      return { success: true, plant: mapped };
    } catch (err) {
      toast.error(err.message || 'Error actualizando planta');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deletePlant = useCallback(async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta planta?')) {
      return { success: false, cancelled: true };
    }

    setLoading(true);
    try {
      await plantService.delete(id);
      setPlants(prev => prev.filter(p => p.id !== id));
      toast.success('Planta eliminada correctamente');
      return { success: true };
    } catch (err) {
      toast.error(err.message || 'Error eliminando planta');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    plants,
    loading,
    error,
    loadPlants,
    createPlant,
    updatePlant,
    deletePlant
  };
};